import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow } from './InventoryShared';

const LOCATION_TYPES = ['receiving', 'storage', 'dispatch', 'returns', 'inspection', 'quarantine', 'damaged', 'repair', 'transit'];

export default function InventoryWarehouses() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [editing, setEditing] = useState(null);
  const [locationsOf, setLocationsOf] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/inventory/warehouses`, { headers: getAuthHeaders() });
      setList(await res.json());
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const removeItem = async (id) => {
    if (!window.confirm('Delete this warehouse?')) return;
    const res = await fetch(`${API}/companies/${companyId}/inventory/warehouses/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🏬 Warehouses & Locations</h2>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Warehouse</button>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Name', 'Code', 'Locations', 'Default', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No warehouses</td></tr>}
              {list.map(w => (
                <tr key={w.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>
                    <a href="#loc" onClick={e => { e.preventDefault(); setLocationsOf(w); }} style={{ color: '#3b82f6' }}>{w.name}</a>
                  </td>
                  <td style={tdStyle}>{w.code || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{w.locations_count ?? 0}</td>
                  <td style={tdStyle}>{w.is_default ? 'Yes' : '—'}</td>
                  <td style={tdStyle}><Badge value={w.is_active ? 'active' : 'inactive'} /></td>
                  <td style={tdStyle}>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setLocationsOf(w)}>Locations</button>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setEditing(w)}>Edit</button>
                    <button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeItem(w.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <WarehouseModal companyId={companyId} warehouse={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />}
      {locationsOf && <LocationsModal companyId={companyId} warehouse={locationsOf} onClose={() => { setLocationsOf(null); fetchList(); }} />}
    </div>
  );
}

function WarehouseModal({ companyId, warehouse, onClose, onSaved }) {
  const isNew = !warehouse.id;
  const [form, setForm] = useState({
    name: warehouse.name || '', code: warehouse.code || '', address: warehouse.address || '',
    is_default: warehouse.is_default || false, location_tracking_enabled: warehouse.location_tracking_enabled ?? true,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/companies/${companyId}/inventory/warehouses` : `${API}/companies/${companyId}/inventory/warehouses/${warehouse.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{isNew ? 'New Warehouse' : 'Edit Warehouse'}</h3>
        <FormRow label="Name *"><input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></FormRow>
        <FormRow label="Code"><input style={inputStyle} value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} /></FormRow>
        <FormRow label="Address"><textarea style={{ ...inputStyle, minHeight: 60 }} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></FormRow>
        <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={form.is_default} onChange={e => setForm({ ...form, is_default: e.target.checked })} /> Default Warehouse
          </label>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={form.location_tracking_enabled} onChange={e => setForm({ ...form, location_tracking_enabled: e.target.checked })} /> Location Tracking
          </label>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function LocationsModal({ companyId, warehouse, onClose }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [form, setForm] = useState({ code: '', name: '', location_type: 'storage', counts_as_on_hand: true, counts_as_available: true });
  const [saving, setSaving] = useState(false);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/companies/${companyId}/inventory/warehouses/${warehouse.id}/locations`, { headers: getAuthHeaders() });
    setLocations(await res.json());
    setLoading(false);
  }, [companyId, warehouse.id]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const addLocation = async () => {
    if (!form.code.trim()) { alert('Code is required'); return; }
    setSaving(true);
    const res = await fetch(`${API}/companies/${companyId}/inventory/warehouses/${warehouse.id}/locations`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
    setForm({ code: '', name: '', location_type: 'storage', counts_as_on_hand: true, counts_as_available: true });
    setSaving(false);
    fetchLocations();
  };

  const removeLocation = async (id) => {
    if (!window.confirm('Delete this location?')) return;
    const res = await fetch(`${API}/companies/${companyId}/inventory/warehouses/${warehouse.id}/locations/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchLocations();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 680 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Locations — {warehouse.name}</h3>

        <table style={tableStyle}>
          <thead><tr>{['Code', 'Name', 'Type', 'On Hand?', 'Available?', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {initialLoading && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
            {!loading && locations.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No locations</td></tr>}
            {locations.map(l => (
              <tr key={l.id}>
                <td style={tdStyle}>{l.code}</td>
                <td style={tdStyle}>{l.name || '—'}</td>
                <td style={tdStyle}>{l.location_type}</td>
                <td style={tdStyle}>{l.counts_as_on_hand ? 'Yes' : 'No'}</td>
                <td style={tdStyle}>{l.counts_as_available ? 'Yes' : 'No'}</td>
                <td style={tdStyle}><button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeLocation(l.id)}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 8 }}>ADD LOCATION</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ width: 100 }}><FormRow label="Code *"><input style={inputStyle} value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} /></FormRow></div>
            <div style={{ width: 140 }}><FormRow label="Name"><input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></FormRow></div>
            <div style={{ width: 140 }}><FormRow label="Type">
              <select style={inputStyle} value={form.location_type} onChange={e => setForm({ ...form, location_type: e.target.value })}>
                {LOCATION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </FormRow></div>
            <label style={{ fontSize: 12, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={form.counts_as_on_hand} onChange={e => setForm({ ...form, counts_as_on_hand: e.target.checked })} /> On Hand
            </label>
            <label style={{ fontSize: 12, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={form.counts_as_available} onChange={e => setForm({ ...form, counts_as_available: e.target.checked })} /> Available
            </label>
            <button style={{ ...btnPrimary, marginBottom: 12 }} onClick={addLocation} disabled={saving}>Add</button>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
          <button style={btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
